import cx from 'classnames';
import { toLower } from 'lodash';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import SVG from 'react-inlinesvg';

import { IMAGES_PATH } from 'constants/images.constants';
import { PaymentType } from 'enums';
import { TranslationNamespace } from 'i18n';

import { SqCheckmark } from '../SqCheckmark';
import { SqLogoImage } from '../SqLogoImage';

const PAYMENT_TYPES_PATH = `${IMAGES_PATH}payment-types`;

const SPECIAL_LOGOS: Record<string, string> = {
  [PaymentType.SberPay]: 'sberpay',
  [PaymentType.TSBP]: 'tsbp',
  [PaymentType.TCard2Card]: 'card2card',
  [PaymentType.TSim]: 'sim',
};

// Sq titles for these types end with the preposition before the bank name
// ("… на {{bank}}"); without a bank the preposition dangles, so fall back to the
// bank-free `payment_types_sq.<type>_any_bank` label.
const BANK_IN_TITLE_PAYMENT_TYPES = new Set<string>([
  PaymentType.Card2Card,
  PaymentType.TCard2Card,
]);

type PaymentTypeIconProps = {
  paymentType: string;
  className?: string;
};

const PaymentTypeIcon: React.FC<PaymentTypeIconProps> = ({
  paymentType,
  className,
}) => {
  const src = useMemo(
    () =>
      `${PAYMENT_TYPES_PATH}/${toLower(
        SPECIAL_LOGOS[paymentType] || paymentType,
      )}.svg`,
    [paymentType],
  );

  return <SVG src={src} className={className} onError={() => undefined} />;
};

type Props = {
  onSelect: () => void;
  bank?: string;
  bankName?: string;
  paymentType: string;
  paymentTypeName: string;
  comment?: string;
  isSelected?: boolean;
};

export const SqPaymentOption: React.FC<Props> = ({
  bank = '',
  bankName = '',
  paymentType,
  paymentTypeName: paymentTypeNameProp,
  comment,
  isSelected = false,
  onSelect,
}) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'components.bank_select',
  });

  const title = useMemo(() => {
    if (!bankName && BANK_IN_TITLE_PAYMENT_TYPES.has(paymentType)) {
      return t(`payment_types_sq.${paymentType}_any_bank`, {
        defaultValue: paymentTypeNameProp,
      });
    }
    // Types without an Sq-specific title fall back to the generic one, so the
    // untranslated backend name is the last resort.
    return t(
      [`payment_types_sq.${paymentType}`, `payment_types.${paymentType}`],
      {
        defaultValue: paymentTypeNameProp,
        bank: bankName,
      },
    );
  }, [t, paymentType, paymentTypeNameProp, bankName]);

  const hasBankIcon = !!bank && !SPECIAL_LOGOS[paymentType];

  return (
    <div
      data-testid={`sq-payment-option-${paymentType}`}
      className={cx(
        'tw-relative tw-flex tw-flex-col tw-mb-3 tw-px-4 tw-py-3 tw-rounded-2xl tw-cursor-pointer tw-transition-colors tw-border tw-border-solid',
        isSelected
          ? 'tw-bg-tertiary-button tw-border-component-accent'
          : 'tw-bg-[var(--bg-color-option-unselected)] tw-border-transparent',
      )}
      onClick={onSelect}
    >
      {isSelected && (
        <div className="tw-absolute tw-top-3 tw-right-4">
          <SqCheckmark />
        </div>
      )}

      <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
        <PaymentTypeIcon
          paymentType={paymentType}
          className="tw-h-8 tw-flex-shrink-0"
        />
        {hasBankIcon && (
          <>
            <span className="tw-w-px tw-h-6 tw-bg-[var(--border-color-secondary,#e0e0e0)] tw-flex-shrink-0" />
            <SqLogoImage
              bank={bank}
              bankName={bankName}
              paymentType={paymentType}
              paymentTypeName={paymentTypeNameProp}
              className="tw-w-8 tw-h-8 tw-flex-shrink-0"
            />
          </>
        )}
      </div>

      <span className="tw-text-base tw-font-semibold tw-text-primary tw-leading-snug tw-pr-8">
        {title}
      </span>

      {comment && (
        <span className="tw-text-sm tw-text-secondary tw-mt-0.5 tw-leading-snug">
          {comment}
        </span>
      )}
    </div>
  );
};
